package floris.graphics.core.utils

import floris.graphics.NetworkingConfig
import floris.graphics.models.domain.models.CredentialTokens
import kotlinx.browser.window
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import org.w3c.dom.set
import kotlin.js.Date

object TokenManager {
    const val TOKEN_KEY = "tokens"
    const val REMEMBER_KEY = "remember"

    fun get(): CredentialTokens? {
        val tokens = window.localStorage.getItem(TOKEN_KEY)
        return tokens?.let(Json::decodeFromString)
    }

    fun getRemember(): Boolean = window.localStorage.getItem(REMEMBER_KEY)?.toBoolean() ?: false

    fun set(tokens: CredentialTokens, remember: Boolean? = null) {
        window.localStorage[TOKEN_KEY] = Json.encodeToString(tokens)
        if (remember != null) {
            window.localStorage[REMEMBER_KEY] = remember.toString()
        }
    }

    fun clear() {
        window.localStorage.removeItem(TOKEN_KEY)
        window.localStorage.removeItem(REMEMBER_KEY)
    }

    /**
     * Checks if the stored tokens are still valid.
     * Returns true if
     * - the access token is not expired
     * - the refresh token is available, can be used (remembered) and not expired
     */
    fun areTokensStillValid(): Boolean {
        val remembered = getRemember()
        val tokens = get() ?: return false

        return try {
            !isTokenExpired(tokens.accessToken) || (remembered && !isTokenExpired(tokens.refreshToken))
        } catch (e: Exception) {
            console.error("Invalid tokens format or content", e)
            false
        }
    }

    private fun isTokenExpired(token: String): Boolean {
        val exp = getTokenExpiration(token) ?: return true
        val nowInSeconds = (Date.now() / 1000).toLong()

        return exp <= nowInSeconds
    }

    @Suppress("MagicNumber")
    private fun getTokenExpiration(token: String): Long? {
        val parts = token.split(".")
        if (parts.size != 3) return null

        val payloadBase64 = parts[1]
            .replace('-', '+')  // Base64 URL-safe to standard
            .replace('_', '/')
            .padEnd((parts[1].length + 3) / 4 * 4, '=')  // Pad to valid base64

        return try {
            val json = window.atob(payloadBase64)
            NetworkingConfig.getJson.parseToJsonElement(json).jsonObject["exp"]?.jsonPrimitive?.longOrNull
        } catch (e: Throwable) {
            console.warn("Failed to decode JWT: ${e.message}")
            null
        }
    }
}
